import { useState } from "react";
import { Badge, Button, Dialog, Field, Icon, Input, Select } from "../../ds/DS";
import type { Tone } from "../../ds/DS";
import { api } from "../../lib/api";
import { fmtCost, fmtDay, type VChangeRequest, type VRelease, type VTicket } from "../data";
import type { PageProps } from "../Portal";
import { id8 } from "../ticketThread";
import { DialogFoot, PageHead, ScopeNote, TabBar } from "./parts";
import { firstName, plural } from "./util";

const CR_STATES = ["Awaiting approval", "Approved", "Delivered", "Declined"];
const NO_TICKET = "None";
const changeTone = (s: string): Tone =>
  (({
    "Awaiting approval": "warning",
    Approved: "teal",
    Delivered: "success",
    Declined: "neutral",
  })[s] as Tone) ?? "neutral";

const ticketLabel = (t: VTicket) => `#${id8(t.id)} ${t.subject}`;
const rateLine = (ratePence: number) => `£${(ratePence / 100).toLocaleString("en-GB")}/h + VAT`;

/** The decision line under a card: who decided, when, and where it's going. */
function decisionLine(cr: VChangeRequest, releases: VRelease[]): string {
  const rel = cr.scheduledReleaseId ? releases.find((r) => r.id === cr.scheduledReleaseId) : undefined;
  const on = (ms: number | null) => (ms ? ` on ${fmtDay(ms)}` : "");
  const by = cr.decidedBy ? ` by ${cr.decidedBy}` : "";
  if (cr.state === "Approved") {
    return `Approved${by}${on(cr.decidedAt)}${rel ? ` · scheduled in ${rel.name}` : ""}`;
  }
  if (cr.state === "Declined") return `Declined${by}${on(cr.decidedAt)}`;
  if (cr.state === "Delivered") {
    if (rel?.status === "Released") {
      return `Live in ${rel.name}${rel.releasedAt ? ` since ${fmtDay(rel.releasedAt)}` : ""}`;
    }
    return `Delivered${on(cr.deliveredAt)}`;
  }
  return "";
}

function ChangeRequestDialog({
  projects,
  tickets,
  defaultProjectId,
  ratePence,
  contact,
  cr,
  onClose,
  onSaved,
}: {
  projects: PageProps["projects"];
  tickets: VTicket[];
  defaultProjectId: string;
  ratePence: number;
  contact: string;
  cr: VChangeRequest | null;
  onClose: () => void;
  onSaved: (ref?: string) => void;
}) {
  const projectName = (id: string) => projects.find((p) => p.id === id)?.name ?? "";
  const [f, setF] = useState(() => ({
    project: projectName(cr?.projectId ?? defaultProjectId),
    hours: cr?.estimateHours == null ? "" : String(cr.estimateHours),
    title: cr?.title ?? "",
    detail: cr?.detail ?? "",
    state: cr?.state ?? "Awaiting approval",
    ticket: (() => {
      const t = cr?.ticketId ? tickets.find((x) => x.id === cr.ticketId) : undefined;
      return t ? ticketLabel(t) : NO_TICKET;
    })(),
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));
  const hours = Number(f.hours) || 0;
  const projectId = projects.find((p) => p.name === f.project)?.id ?? "";
  const ticketOptions = [
    NO_TICKET,
    ...tickets
      .filter((t) => t.projectId === projectId || t.id === cr?.ticketId)
      .map(ticketLabel),
  ];
  const invalid = !f.title.trim() || hours <= 0 || !projectId;

  const save = async () => {
    if (invalid) return;
    setBusy(true);
    setErr("");
    const ticketId =
      f.ticket === NO_TICKET ? null : (tickets.find((t) => ticketLabel(t) === f.ticket)?.id ?? null);
    const payload = {
      title: f.title.trim(),
      detail: f.detail,
      estimateHours: hours,
      ticketId,
    };
    try {
      if (cr) {
        await api.patch(`/change-requests/${cr.id}`, { ...payload, state: f.state });
        onSaved();
      } else {
        const r = await api.post<{ changeRequest: { ref: string } }>(
          `/projects/${projectId}/change-requests`,
          payload,
        );
        onSaved(r.changeRequest?.ref);
      }
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save the change request. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title={cr ? "Edit change request" : "New change request"}
      description={`${contact} approves or declines it from the portal. Nothing gets built until it's approved.`}
      accent
      width={560}
      onClose={onClose}
      footer={
        <DialogFoot
          saveLabel={cr ? "Save" : "Send for approval"}
          busy={busy}
          invalid={invalid}
          onCancel={onClose}
          onSave={() => void save()}
        />
      }
    >
      <div className="stack-16">
        {err ? <div className="form-err">{err}</div> : null}
        <div className="grid-2">
          <Field label="Project">
            <Select
              options={projects.map((p) => p.name)}
              value={f.project}
              disabled={!!cr}
              onChange={(e) => setF((s) => ({ ...s, project: e.target.value, ticket: NO_TICKET }))}
            />
          </Field>
          <Field
            label="Estimate (hours)"
            hint={
              hours > 0
                ? `${fmtCost(Math.round(hours * ratePence))} at ${rateLine(ratePence)}`
                : `Billed at £${(ratePence / 100).toLocaleString("en-GB")}/h.`
            }
          >
            <Input
              type="number"
              step="0.5"
              min={0}
              value={f.hours}
              onChange={(e) => set("hours", e.target.value)}
            />
          </Field>
        </div>
        <Field label="Title" required>
          <Input value={f.title} onChange={(e) => set("title", e.target.value)} />
        </Field>
        <Field label="What changes">
          <Input
            multiline
            rows={3}
            placeholder={`Describe it the way ${contact} would.`}
            value={f.detail}
            onChange={(e) => set("detail", e.target.value)}
          />
        </Field>
        <div className="grid-2">
          <Field label="From ticket">
            <Select
              options={ticketOptions}
              value={ticketOptions.includes(f.ticket) ? f.ticket : NO_TICKET}
              onChange={(e) => set("ticket", e.target.value)}
            />
          </Field>
          {cr ? (
            <Field label="State">
              <Select
                options={CR_STATES}
                value={f.state}
                onChange={(e) => set("state", e.target.value)}
              />
            </Field>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}

export function Changes({
  isAdmin,
  client,
  changeRequests,
  projects,
  tickets,
  releases,
  crRatePence,
  pid,
  setPid,
  openItem,
  reload,
  toast,
  projectName,
}: PageProps) {
  const scoped =
    pid === "all" ? changeRequests : changeRequests.filter((cr) => cr.projectId === pid);
  const [tab, setTab] = useState<"open" | "approved" | "all">("open");
  const [dialog, setDialog] = useState<{ cr: VChangeRequest | null } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const contact = client?.contact ? firstName(client.contact) : "The client";

  const pending = scoped.filter((c) => c.state === "Awaiting approval");
  const approved = scoped.filter((c) => c.state === "Approved" || c.state === "Delivered");
  const rows = tab === "open" ? pending : tab === "approved" ? approved : scoped;
  const pendingTotal = pending.reduce((n, c) => n + (c.costPence ?? 0), 0);

  const act = async (cr: VChangeRequest, run: () => Promise<unknown>, msg: string) => {
    setBusyId(cr.id);
    try {
      await run();
      reload();
      toast(msg);
    } catch (e) {
      toast(e instanceof Error ? e.message : "That didn't work. Try again.");
    } finally {
      setBusyId(null);
    }
  };
  const ref = (cr: VChangeRequest) => cr.ref || "Change request";
  const decide = (cr: VChangeRequest, ok: boolean) =>
    act(
      cr,
      () => api.patch(`/change-requests/${cr.id}`, { state: ok ? "Approved" : "Declined" }),
      ok
        ? `${ref(cr)} approved. We'll confirm the release date by tomorrow.`
        : `${ref(cr)} declined. Nothing will be billed.`,
    );

  const addProjectId = pid !== "all" ? pid : (projects[0]?.id ?? "");

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="CHANGE REQUESTS"
        title="Change requests"
        sub={
          isAdmin
            ? "Extra work beyond the agreed scope. Quoted up front, scheduled once approved."
            : "Extra work beyond what we agreed. Nothing is built or billed until you approve it."
        }
        actions={
          isAdmin && addProjectId ? (
            <Button icon="plus" onClick={() => setDialog({ cr: null })}>
              New change request
            </Button>
          ) : undefined
        }
      />
      <ScopeNote pid={pid} setPid={setPid} projectName={projectName} />

      <TabBar
        items={[
          { value: "open", label: "Awaiting approval", count: pending.length },
          { value: "approved", label: "Approved", count: approved.length },
          { value: "all", label: "All", count: scoped.length },
        ]}
        value={tab}
        onChange={(v) => setTab(v as "open" | "approved" | "all")}
        aside={
          pending.length ? `${fmtCost(pendingTotal)} awaiting approval` : "Nothing awaiting approval"
        }
      />

      {rows.length === 0 ? (
        <div className="list-card">
          <div className="list-blank">
            <div className="list-blank-title">Nothing here</div>
            <div className="list-blank-line">No change requests in this view.</div>
          </div>
        </div>
      ) : (
        rows.map((cr) => {
          const isPending = cr.state === "Awaiting approval";
          const busy = busyId === cr.id;
          const decided = decisionLine(cr, releases);
          const ticket = cr.ticketId ? tickets.find((t) => t.id === cr.ticketId) : undefined;
          return (
            <div key={cr.id} className={"crcard" + (isPending ? " is-active" : "")}>
              <div className="crcard-main">
                <div className="row-8">
                  {cr.ref ? <span className="crcard-ref mono">{cr.ref}</span> : null}
                  <Badge tone={changeTone(cr.state)} dot={isPending}>
                    {cr.state}
                  </Badge>
                  <span className="mono crcard-proj">{projectName(cr.projectId)}</span>
                </div>
                <div className="crcard-title">{cr.title}</div>
                {cr.detail ? <p className="crcard-detail">{cr.detail}</p> : null}
                <div className="crcard-meta mono">
                  <span>Raised {cr.raised}</span>
                  {cr.ticketId ? (
                    <button
                      type="button"
                      className="crcard-ticket"
                      title={ticket?.subject}
                      onClick={() => openItem("tickets", cr.ticketId as string)}
                    >
                      From ticket #{id8(cr.ticketId)} →
                    </button>
                  ) : null}
                  {isAdmin && isPending && cr.remindedAt ? (
                    <span>Reminded {fmtDay(cr.remindedAt)}</span>
                  ) : null}
                </div>
                {decided ? (
                  <div className={"crcard-decided" + (cr.state === "Declined" ? " is-no" : "")}>
                    <Icon name={cr.state === "Declined" ? "x" : "check"} size={15} />
                    {decided}
                  </div>
                ) : null}
              </div>
              <div className="crcard-side">
                <span className="stat-label">Estimate</span>
                <div>
                  <div className="crcard-cost">{cr.cost || "—"}</div>
                  {cr.estimateHours != null ? (
                    <div className="crcard-hours mono">
                      {plural(cr.estimateHours, "hour")} at {rateLine(crRatePence)}
                    </div>
                  ) : null}
                </div>
                <div className="crcard-actions">
                  {!isAdmin && isPending ? (
                    <>
                      <Button
                        size="sm"
                        icon="check"
                        disabled={busy}
                        onClick={() => void decide(cr, true)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        icon="x"
                        disabled={busy}
                        onClick={() => void decide(cr, false)}
                      >
                        Decline
                      </Button>
                    </>
                  ) : null}
                  {isAdmin && cr.state === "Approved" ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon="rocket"
                      disabled={busy}
                      onClick={() =>
                        void act(
                          cr,
                          () => api.patch(`/change-requests/${cr.id}`, { state: "Delivered" }),
                          `${ref(cr)} delivered.`,
                        )
                      }
                    >
                      Mark delivered
                    </Button>
                  ) : null}
                  {isAdmin && isPending ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon="bell"
                      disabled={busy}
                      onClick={() =>
                        void act(
                          cr,
                          () => api.post(`/change-requests/${cr.id}/remind`),
                          `Reminder sent to ${client?.contact || "the client"}.`,
                        )
                      }
                    >
                      Send reminder
                    </Button>
                  ) : null}
                  {isAdmin ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      icon="pencil"
                      onClick={() => setDialog({ cr })}
                    >
                      Edit
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })
      )}

      {dialog ? (
        <ChangeRequestDialog
          projects={projects}
          tickets={tickets}
          defaultProjectId={addProjectId}
          ratePence={crRatePence}
          contact={contact}
          cr={dialog.cr}
          onClose={() => setDialog(null)}
          onSaved={(newRef) => {
            reload();
            if (!dialog.cr) setTab("open");
            toast(
              dialog.cr
                ? "Change request updated."
                : `${newRef ?? "Change request"} sent to ${contact} for approval.`,
            );
          }}
        />
      ) : null}
    </div>
  );
}
